import React, { Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader } from "../Components/Loader";
import PermissionGuard from "../Components/PermissionGuard";
import Dashboard from "../Pages/Dashboard";

import CreateProduct from "../Pages/Product/Create";
import ListProduct from "../Pages/Product/List";
import ProductDetails from "../Pages/Product/View/index";

import CreateCategory from "../Pages/Category/Create";
import ListCategory from "../Pages/Category/List";

import CreateLooping from "../Pages/Looping/Create";
import ListLooping from "../Pages/Looping/List";

import CreateDepartment from "../Pages/Department/Create";
import ListDepartment from "../Pages/Department/List";

import CreateCoursing from "../Pages/Coursing/Create";
import ListCoursing from "../Pages/Coursing/List";

import CreateProductBrand from "../Pages/ProductBrand/Create";
import ListProductBrand from "../Pages/ProductBrand/List";

import CreateProductTags from "../Pages/ProductTags/Create";
import ListProductTags from "../Pages/ProductTags/List";

import CreateCustomer from "../Pages/Customer/Create";
import ListCustomer from "../Pages/Customer/List";
import CustomerTransaction from "../Pages/CustomerTransaction";

import CreateTax from "../Pages/Tax/Create";
import ListTax from "../Pages/Tax/List";


import ViewBooking from "../Pages/Booking/View";
import ListBooking from "../Pages/Booking/List";

import ViewDriverBooking from "../Pages/DriverBooking/View";

import CreatePrinter from "../Pages/Printer/Create";
import ListPrinter from "../Pages/Printer/List";

import CreateCompany from "../Pages/CompanyInfo/Create";
import ListCompany from "../Pages/CompanyInfo/List";
import EditCompany from "../Pages/CompanyInfo/Edit";
import BusinessProfile from "../Pages/BusinessProfile";

import CreateQuote from "../Pages/Quote/Create";
import ListQuote from "../Pages/Quote/List";
import EditQuote from "../Pages/Quote/Edit";
import TerminateQuote from "../Pages/Quote/Terminate";

import HistoryWorkOrder from "../Pages/WorkOrder/History";

import HistoryInvoice from "../Pages/Invoice/History";
import InvoiceList from "../Pages/Invoice/List";
import CreateInvoice from "../Pages/Invoice/Create";
import RedirectInvoiceEditToQuoteHistory from "../Pages/Invoice/Edit/redirectToQuoteHistory";

import CreateDriver from "../Pages/Driver/Create";
import ListDriver from "../Pages/Driver/List";
import DriverHistory from "../Pages/Driver/View";

import CustomerProject from "../Pages/CustomerTransaction/Create";
import InvoceCreateWithProject from "../Pages/Invoice/Create/invoceCreateWithProject";
import InvoceEditWithProject from "../Pages/Invoice/Edit/invoceEditWithProject";

import ListEmail from "../Pages/Email/List";
import CreateEmail from "../Pages/Email/Create";

import ListSms from "../Pages/Sms/List";
import CreateSms from "../Pages/Sms/Create";

import ListNote from "../Pages/Note/List";
import CreateNote from "../Pages/Note/Create";

import CreateExpense from "../Pages/Expense/Create";
import ListExpense from "../Pages/Expense/List";
import EditExpense from "../Pages/Expense/Edit";
import ExpenseDashboard from "../Pages/Expense/Dashboard";
import ExpenseReports from "../Pages/Expense/Reports";

import ListDashboard from "../Pages/Dashboard/List";

import CreateExpenseCategory from "../Pages/ExpenseCategory/Create";
import ListExpenseCategory from "../Pages/ExpenseCategory/List";
import EditExpenseCategory from "../Pages/ExpenseCategory/Edit";

import CreateDeposit from "../Pages/Deposit/Create";
import ListDeposit from "../Pages/Deposit/List";
import EditDeposit from "../Pages/Deposit/Edit";
import ListReport from "../Pages/Report/List";
import BalanceSheet from "../Pages/Reports/BalanceSheet";
import ListTour from "../Pages/Tour/List";
import DriverPaymentList from "../Pages/DriverPayment/List";
import DriverReviewsList from "../Pages/DriverPayment/Reviews";
import EnquiryList from "../Pages/Enquiry/List";
import EnquiryDetail from "../Pages/Enquiry/Detail";
import EnquiryCalendarPage from "../Pages/Enquiry/CalendarPage";
import DriverPayrollPage from "../Pages/DriverPayroll";

import CreateUrl from "../Pages/Url/Create";
import ListUrl from "../Pages/Url/List";

import ListProject from "../Pages/Project/List";
import APEmailPage from "../Pages/AP";
import CreateContractInvoice from "../Pages/ContractInvoice/Create";
import ListContractInvoice from "../Pages/ContractInvoice/List";
import ViewContractInvoice from "../Pages/ContractInvoice/View";
import ContractList from "../Pages/Contract/List";
import ContractCreate from "../Pages/Contract/Create";
import ContractBillingDashboard from "../Pages/ContractBillingDashboard";
import UsersPage from "../Pages/UserManagement/Users";
import UserProfilePage from "../Pages/UserManagement/Users/Profile";
import UserRolesPage from "../Pages/UserManagement/UserRoles";
import PrivilegesPage from "../Pages/UserManagement/Privileges";
import OnlinePaymentsPage from "../Pages/OnlinePayments";

// Route-to-module permission mapping: path prefix → module_key
const ROUTE_PERMISSIONS: Record<string, string> = {
  "/customer":          "customers",
  "/quote":             "quotes",
  "/quote-history":     "quotes",
  "/invoice":           "invoices",
  "/contract-invoice":  "contract_invoices",
  "/contract":          "contract_invoices",
  "/ap":                "ap_email",
  "/driverBooking":     "trips",
  "/booking":           "trips",
  "/tour":              "drivers",
  "/team":              "drivers",
  "/driver-payment":    "driver_payments",
  "/driver-payroll":    "driver_payroll",
  "/enquiry":           "leads",
  "/report":            "reports",
  "/reports":           "reports",
  "/expense":           "expenses",
  "/expense-category":  "expenses",
  "/deposit":           "deposits",
  "/company":           "companies",
  "/business-profile":  "settings",
  "/product":           "products",
  "/category":          "categories",
  "/email":             "email",
  "/sms":               "sms",
  "/note":              "notes",
  "/user-management":   "users",
  "/work-order":        "invoices",
  "/online-payments":   "online_payments",
};

/** Checks the current URL path against the permission map and blocks if unauthorized */
const RoutePermissionGuard = ({ children }: { children: React.ReactNode }) => {
  const { pathname } = useLocation();
  // eslint-disable-next-line no-console
  React.useEffect(() => { console.log("ROUTE ACCESS:", pathname); }, [pathname]);
  const prefix = Object.keys(ROUTE_PERMISSIONS)
    .sort((a, b) => b.length - a.length)
    .find((p) => pathname.startsWith(p));
  if (!prefix) return <>{children}</>;
  return <PermissionGuard module={ROUTE_PERMISSIONS[prefix]}>{children}</PermissionGuard>;
};

/**
 * AppRoutes will load the app routes.
 * @returns
 */
const AppRoutes = () => {
  return (
    <RoutePermissionGuard>
    <Routes>
      <Route
        element={
          <Suspense fallback={<Loader />}>{/* <Fallback /> */}</Suspense>
        }
        path="/fallback"
      />
      {/* Root route → Dashboard. The previous duplicate <Route path="/"> that
          pointed at /expense/list was unreachable in React Router v6 (only
          the first match wins) but caused confusion when triaging redirect
          loops. Removed. */}
      <Route
        path="/"
        element={
          <Suspense fallback={<Loader />}>
            <Navigate to="/dashboard/list" replace />
          </Suspense>
        }
      />
      <Route
        path="/dashboard"
        element={
          <Suspense fallback={<Loader />}>
            <Dashboard />
          </Suspense>
        }
      />
 <Route
        path="/dashboard/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListDashboard />
          </Suspense>
        }
      />

      <Route
        path="/product/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListProduct />
          </Suspense>
        }
      />
      <Route
        path="/product/create"
        element={
          <Suspense fallback={<Loader />}>
            <CreateProduct />
          </Suspense>
        }
      />
      <Route
        path="/category/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListCategory />
          </Suspense>
        }
      />
      <Route
        path="/category/create"
        element={
          <Suspense fallback={<Loader />}>
            <CreateCategory />
          </Suspense>
        }
      />
        <Route
        path="/expense/dashboard"
        element={
          <Suspense fallback={<Loader />}>
            <ExpenseDashboard />
          </Suspense>
        }
      />
      <Route
        path="/expense/reports"
        element={
          <Suspense fallback={<Loader />}>
            <ExpenseReports />
          </Suspense>
        }
      />
        <Route
        path="/expense/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListExpense />
          </Suspense>
        }
      />
      <Route
        path="/expense/create"
        element={
          <Suspense fallback={<Loader />}>
            <CreateExpense />
          </Suspense>
        }
      />
       <Route
        path="/expense/:id"
        element={
          <Suspense fallback={<Loader />}>
            <EditExpense />
          </Suspense>
        }
      />

<Route
        path="/expense-category/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListExpenseCategory />
          </Suspense>
        }
      />
      <Route
        path="/expense-category/create"
        element={
          <Suspense fallback={<Loader />}>
            <CreateExpenseCategory />
          </Suspense>
        }
      />
       <Route
        path="/expense-category/:id"
        element={
          <Suspense fallback={<Loader />}>
            <EditExpenseCategory />
          </Suspense>
        }
      />

<Route
        path="/deposit/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListDeposit />
          </Suspense>
        }
      />
      <Route
        path="/deposit/edit/:id"
        element={
          <Suspense fallback={<Loader />}>
            <EditDeposit />
          </Suspense>
        }
      />
<Route
        path="/report/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListReport />
          </Suspense>
        }
      />
      <Route
        path="/reports/balance-sheet"
        element={
          <Suspense fallback={<Loader />}>
            <BalanceSheet />
          </Suspense>
        }
      />

<Route
        path="/tour/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListTour />
          </Suspense>
        }
      />

      <Route
        path="/driver-payment/list"
        element={
          <Suspense fallback={<Loader />}>
            <DriverPaymentList />
          </Suspense>
        }
      />

      <Route
        path="/driver-payment/reviews"
        element={
          <Suspense fallback={<Loader />}>
            <DriverReviewsList />
          </Suspense>
        }
      />

      <Route
        path="/enquiry/list"
        element={
          <Suspense fallback={<Loader />}>
            <EnquiryList />
          </Suspense>
        }
      />
      <Route
        path="/enquiry/calendar"
        element={
          <Suspense fallback={<Loader />}>
            <EnquiryCalendarPage />
          </Suspense>
        }
      />
      <Route
        path="/enquiry/:id"
        element={
          <Suspense fallback={<Loader />}>
            <EnquiryDetail />
          </Suspense>
        }
      />

      <Route
        path="/driver-payroll"
        element={
          <Suspense fallback={<Loader />}>
            <DriverPayrollPage />
          </Suspense>
        }
      />

      <Route
        path="/deposit/create"
        element={
          <Suspense fallback={<Loader />}>
            <CreateDeposit />
          </Suspense>
        }
      />
    
      <Route
        path="/looping/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListLooping />
          </Suspense>
        }
      />
      <Route
        path="/looping/create"
        element={
          <Suspense fallback={<Loader />}>
            <CreateLooping />
          </Suspense>
        }
      />
      <Route
        path="/department/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListDepartment />
          </Suspense>
        }
      />
      <Route
        path="/department/create"
        element={
          <Suspense fallback={<Loader />}>
            <CreateDepartment />
          </Suspense>
        }
      />
      <Route
        path="/coursing/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListCoursing />
          </Suspense>
        }
      />
      <Route
        path="/coursing/create"
        element={
          <Suspense fallback={<Loader />}>
            <CreateCoursing />
          </Suspense>
        }
      />
      <Route
        path="/brand/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListProductBrand />
          </Suspense>
        }
      />
      <Route
        path="/brand/create"
        element={
          <Suspense fallback={<Loader />}>
            <CreateProductBrand />
          </Suspense>
        }
      />
      <Route
        path="/tag/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListProductTags />
          </Suspense>
        }
      />
      <Route
        path="/tag/create"
        element={
          <Suspense fallback={<Loader />}>
            <CreateProductTags />
          </Suspense>
        }
      />
      <Route
        path="/customer/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListCustomer />
          </Suspense>
        }
      />
      <Route
        path="/project/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListProject />
          </Suspense>
        }
      />

<Route
        path="/booking/view"
        element={
          <Suspense fallback={<Loader />}>
            <ViewBooking />
          </Suspense>
        }
      />
      <Route
        path="/booking/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListBooking />
          </Suspense>
        }
      />

<Route
        path="/driverBooking/list"
        element={
          <Suspense fallback={<Loader />}>
            <ViewDriverBooking />
          </Suspense>
        }
      />

      <Route
        path="/customer/create"
        element={
          <Suspense fallback={<Loader />}>
            <CreateCustomer />
          </Suspense>
        }
      />
      <Route
        path="/tax/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListTax />
          </Suspense>
        }
      />
      <Route
        path="/tax/create"
        element={
          <Suspense fallback={<Loader />}>
            <CreateTax />
          </Suspense>
        }
      />
       <Route
        path="/url/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListUrl />
          </Suspense>
        }
      />
      <Route
        path="/url/create"
        element={
          <Suspense fallback={<Loader />}>
            <CreateUrl />
          </Suspense>
        }
      />
      <Route
        path="/printer/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListPrinter />
          </Suspense>
        }
      />
      <Route
        path="/printer/create"
        element={
          <Suspense fallback={<Loader />}>
            <CreatePrinter />
          </Suspense>
        }
      />
      <Route
        path="/customer-history/:id"
        element={
          <Suspense fallback={<Loader />}>
            <CustomerTransaction />
          </Suspense>
        }
      />
      <Route
        path="/product-history/:id"
        element={
          <Suspense fallback={<Loader />}>
            <ProductDetails />
          </Suspense>
        }
      />
      <Route
        path="/business-profile"
        element={
          <Suspense fallback={<Loader />}>
            <BusinessProfile />
          </Suspense>
        }
      />
      <Route
        path="/company/create"
        element={
          <Suspense fallback={<Loader />}>
            <CreateCompany />
          </Suspense>
        }
      />
      <Route
        path="/company/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListCompany />
          </Suspense>
        }
      />
      <Route
        path="/company-history/:id"
        element={
          <Suspense fallback={<Loader />}>
            <EditCompany />
          </Suspense>
        }
      />
      <Route
        path="/quote/create"
        element={
          <Suspense fallback={<Loader />}>
            <CreateQuote />
          </Suspense>
        }
      />

     <Route
        path="/quote/create/:id"
        element={
          <Suspense fallback={<Loader />}>
            <CreateQuote />
          </Suspense>
        }
      />
      <Route
        path="/quote/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListQuote />
          </Suspense>
        }
      />
      <Route
        path="/quote/terminate"
        element={
          <Suspense fallback={<Loader />}>
            <TerminateQuote />
          </Suspense>
        }
      />
      <Route
        path="/quote-history/:id"
        element={
          <Suspense fallback={<Loader />}>
            <EditQuote />
          </Suspense>
        }
      />
      <Route
        path="/work-order/history/:id"
        element={
          <Suspense fallback={<Loader />}>
            <HistoryWorkOrder />
          </Suspense>
        }
      />
      <Route
        path="/invoice/history/:id"
        element={
          <Suspense fallback={<Loader />}>
            <HistoryInvoice />
          </Suspense>
        }
      />
      <Route
        path="/invoice/list"
        element={
          <Suspense fallback={<Loader />}>
            <InvoiceList />
          </Suspense>
        }
      />
      <Route
        path="/team/create"
        element={
          <Suspense fallback={<Loader />}>
            <CreateDriver />
          </Suspense>
        }
      />
      <Route
        path="/team/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListDriver />
          </Suspense>
        }
      />
      <Route
        path="/team-history/:id"
        element={
          <Suspense fallback={<Loader />}>
            <DriverHistory />
          </Suspense>
        }
      />
      <Route
        path="/invoice/create"
        element={
          <Suspense fallback={<Loader />}>
            <CreateInvoice />
          </Suspense>
        }
      />
      <Route
        path="/invoice/create/:id"
        element={
          <Suspense fallback={<Loader />}>
            <CreateInvoice />
          </Suspense>
        }
      />

<Route
        path="/invoice/edit/:id"
        element={
          <Suspense fallback={<Loader />}>
            <RedirectInvoiceEditToQuoteHistory />
          </Suspense>
        }
      />



       <Route
        path="/invoice/project/edit/:id"
        element={
          <Suspense fallback={<Loader />}>
            <InvoceEditWithProject />
          </Suspense>
        }
      />

      <Route
        path="/customer-history/project/:id"
        element={
          <Suspense fallback={<Loader />}>
            <CustomerProject />
          </Suspense>
        }
      />
      <Route
        path="/invoice/create/customer/:customerId/project/:projectId"
        element={
          <Suspense fallback={<Loader />}>
            <InvoceCreateWithProject />
          </Suspense>
        }
      />
     
      <Route
        path="/email/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListEmail />
          </Suspense>
        }
      />
    
      <Route
        path="/email/create"
        element={
          <Suspense fallback={<Loader />}>
            <CreateEmail />
          </Suspense>
        }
      />

        <Route
        path="/sms/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListSms />
          </Suspense>
        }
      />
      <Route
        path="/sms/create"
        element={
          <Suspense fallback={<Loader />}>
            <CreateSms />
          </Suspense>
        }
      />

      <Route
        path="/note/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListNote />
          </Suspense>
        }
      />
    
      <Route
        path="/note/create"
        element={
          <Suspense fallback={<Loader />}>
            <CreateNote />
          </Suspense>
        }
      />

      <Route
        path="/ap/email"
        element={
          <Suspense fallback={<Loader />}>
            <APEmailPage />
          </Suspense>
        }
      />

      {/* ── Recurring Contract routes ── */}
      <Route path="/contract/list" element={<Suspense fallback={<Loader />}><ContractList /></Suspense>} />
      <Route path="/contract/create" element={<Suspense fallback={<Loader />}><ContractCreate /></Suspense>} />
      <Route path="/contract/edit/:id" element={<Suspense fallback={<Loader />}><ContractCreate /></Suspense>} />

      {/* ── Contract Billing Dashboard ── */}
      <Route
        path="/contract/billing-dashboard"
        element={
          <Suspense fallback={<Loader />}>
            <ContractBillingDashboard />
          </Suspense>
        }
      />

      {/* ── Contract Invoice routes ── */}
      <Route
        path="/contract-invoice/list"
        element={
          <Suspense fallback={<Loader />}>
            <ListContractInvoice />
          </Suspense>
        }
      />
      <Route
        path="/contract-invoice/create"
        element={
          <Suspense fallback={<Loader />}>
            <CreateContractInvoice />
          </Suspense>
        }
      />
      <Route
        path="/contract-invoice/edit/:id"
        element={
          <Suspense fallback={<Loader />}>
            <CreateContractInvoice />
          </Suspense>
        }
      />
      <Route
        path="/contract-invoice/create/client/:clientId"
        element={
          <Suspense fallback={<Loader />}>
            <CreateContractInvoice />
          </Suspense>
        }
      />
      <Route
        path="/contract-invoice/create/from-contract/:clientId/:contractId"
        element={
          <Suspense fallback={<Loader />}>
            <CreateContractInvoice />
          </Suspense>
        }
      />
      <Route
        path="/contract-invoice/view/:id"
        element={
          <Suspense fallback={<Loader />}>
            <ViewContractInvoice />
          </Suspense>
        }
      />

      {/* ── Online Payments (Easy Pay portal transactions) ── */}
      <Route
        path="/online-payments"
        element={
          <Suspense fallback={<Loader />}>
            <OnlinePaymentsPage />
          </Suspense>
        }
      />

      {/* ── User Management ── */}
      <Route path="/user-management/users"
        element={<Suspense fallback={<Loader />}><UsersPage /></Suspense>} />
      <Route path="/user-management/users/:id/profile"
        element={<Suspense fallback={<Loader />}><UserProfilePage /></Suspense>} />
      <Route path="/user-management/roles"
        element={<Suspense fallback={<Loader />}><UserRolesPage /></Suspense>} />
      <Route path="/user-management/privileges"
        element={<Suspense fallback={<Loader />}><PrivilegesPage /></Suspense>} />

    </Routes>
    </RoutePermissionGuard>
  );
};

export default AppRoutes;
